import React from 'react';
import { useTranslation } from 'localization';
import { ApproachChildTemplate } from 'types/approach';
import './ChildrenSummaryMolecule.scss';

export interface ChildrenSummaryMoleculeProps {
  children: ApproachChildTemplate[];
  resolvedSchemaNames?: Map<string, string>;
  className?: string;
}

interface ChildGroup {
  schemaSystemName: string;
  creationMode: 'auto' | 'recommended';
  items: ApproachChildTemplate[];
}

function formatSystemName(systemName: string): string {
  return systemName
    .replace(/^center_/, '')
    .replaceAll('_', ' ')
    .replaceAll(/\b\w/g, (c) => c.toUpperCase());
}

function groupChildren(children: ApproachChildTemplate[]): ChildGroup[] {
  const map = new Map<string, ChildGroup>();
  for (const child of children) {
    const key = `${child.schemaSystemName}__${child.creationMode}`;
    const existing = map.get(key);
    if (existing) {
      existing.items.push(child);
    } else {
      map.set(key, {
        schemaSystemName: child.schemaSystemName,
        creationMode: child.creationMode,
        items: [child],
      });
    }
  }
  return Array.from(map.values());
}

const ChildrenSummaryMolecule: React.FC<ChildrenSummaryMoleculeProps> = ({
  children: childTemplates,
  resolvedSchemaNames,
  className = '',
}) => {
  const { t } = useTranslation();

  const classes = ['marketplace-children-summary-molecule', className]
    .filter(Boolean)
    .join(' ');

  if (childTemplates.length === 0) {
    return null;
  }

  const groups = groupChildren(childTemplates);

  return (
    <div className={classes}>
      <div className="marketplace-children-summary-molecule__header">
        <span>
          {t('marketplace.approachDetail.children.childCount', {
            count: childTemplates.length,
          })}
        </span>
      </div>

      <div className="marketplace-children-summary-molecule__groups">
        {groups.map((group) => (
          <div
            key={`${group.schemaSystemName}__${group.creationMode}`}
            className="marketplace-children-summary-molecule__group"
          >
            <div className="marketplace-children-summary-molecule__group-header">
              <i className="pi pi-sitemap" />
              <span className="marketplace-children-summary-molecule__schema-name">
                {resolvedSchemaNames?.get(group.schemaSystemName) ??
                  formatSystemName(group.schemaSystemName)}
              </span>
              <span className="marketplace-children-summary-molecule__count">
                &times;{group.items.length}
              </span>
            </div>

            <span className="marketplace-children-summary-molecule__mode-badge">
              {group.creationMode === 'auto'
                ? t('marketplace.approachDetail.children.creationModes.autoCreated')
                : t('marketplace.approachDetail.children.creationModes.recommended')}
            </span>

            {group.items.map((child) =>
              child.condition ? (
                <div
                  key={child.id}
                  className="marketplace-children-summary-molecule__condition"
                >
                  <i className="pi pi-filter" />
                  <span>
                    {t('marketplace.approachDetail.children.conditionLabel', {
                      condition: child.condition.property,
                    })}
                  </span>
                </div>
              ) : null
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChildrenSummaryMolecule;
